import { useLayoutEffect, useMemo, useState } from 'react'
import {
  Expression,
  ExpressionBinaryOperator,
  ExpressionType,
  ShapeBase,
} from '~/prix/types/expression'
import FiltersComponent, { PossiblePropertyType } from './filter'
import styled from 'styled-components'
import { ButtonVariants } from '~/components/buttonVariants'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons'
import { ZodSchemaInfo } from '~/prix/types/zodType'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: fit-content;
  width: 100%;

  h3 {
    font-size: 0.8rem;
    color: ${({ theme }) => theme.colors.darkGrey};
    font-weight: 600;
  }
`

const ButtonAddExpression = styled(ButtonVariants)`
  font-weight: 600;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    margin-bottom: 15px;
  }
`

const ExpandableHeader = styled.div`
  display: flex;
  flex-direction: column;
  cursor: pointer;
  gap: ${({ theme }) => theme.padding(1)};
  align-items: start;

  .header {
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.padding(1)};
    width: 100%;
  }

  .expandable-line {
    flex-grow: 1;
    border: none;
    border-top: ${({ theme }) => theme.colors.mediumGrey} 1px solid;
  }

  .expandable-arrow {
    margin-left: 8px;
  }

  .expressions {
    min-height: 50px;
  }
`

interface ExpressionsContainerProps {
  filtersApplied: Expression[] | undefined
  getFilter: (expression: Record<number, Expression> | undefined, index: string) => void
  isLoading: boolean
  filtersAvailable: ExpressionType<Expression<string>, ShapeBase> | undefined
  selectedVariableIsFilterableBy: ZodSchemaInfo | undefined
  resizeHeight: () => void
}

const initialExpression = {
  kind: 'binary' as 'binary',
  type: 'expression' as 'expression',
  left: {} as Expression<string>,
  operator: {} as ExpressionBinaryOperator,
  right: {} as Expression<string>,
}

const ExpressionsContainer = ({
  filtersApplied,
  getFilter,
  isLoading,
  filtersAvailable,
  selectedVariableIsFilterableBy,
  resizeHeight,
}: ExpressionsContainerProps) => {
  const [canAddNewExpression, setCanAddNewExpression] = useState(false)
  const [isExpanded, setIsExpanded] = useState(true)

  const propertiesKeys = useMemo(
    () => Object.keys(selectedVariableIsFilterableBy || {}) as string[],
    [selectedVariableIsFilterableBy],
  )

  const possibleProperties = useMemo(() => {
    return propertiesKeys.map(key => {
      const filterableValue = selectedVariableIsFilterableBy?.[
        key as keyof ZodSchemaInfo
      ] as ZodSchemaInfo
      return {
        value: {
          ...filterableValue,
          key: key as string,
          type: ['string', 'number', 'boolean'].includes(filterableValue.type)
            ? filterableValue.type
            : 'string',
          subType: filterableValue.type,
        },
        label: filterableValue?.label || '',
      }
    })
  }, [filtersAvailable, propertiesKeys])

  const [expressions, setExpressions] = useState<Record<number, Expression> | undefined>(() => {
    if (filtersApplied?.length && filtersApplied?.length > 0) {
      const filtersArray = filtersApplied.reduce((acc, expression, index) => {
        acc[index] = expression
        return acc
      }, {} as Record<number, Expression>)
      setCanAddNewExpression(true)
      return filtersArray
    }
    return {
      0: initialExpression,
    }
  })

  useLayoutEffect(() => {
    resizeHeight()
  }, [expressions, isExpanded])

  const handleNewExpression = (expression: Expression, index: number) => {
    if (!expressions) return
    const updateExpression = { ...expressions[index] }
    if (updateExpression.kind === 'binary' && expression.kind === 'binary') {
      updateExpression.left = expression.left as Expression
      updateExpression.operator = expression.operator
      updateExpression.right = expression.right as Expression
    }
    const withIndex = { [index]: updateExpression }
    const updatedExpressions = { ...expressions, ...withIndex }

    setExpressions(updatedExpressions)
    setCanAddNewExpression(true)
    getFilter(updatedExpressions, index.toString())

    if (expression.kind === 'binary' && !expression.left) {
      setCanAddNewExpression(false)
    }
  }

  const removeExpression = (index: string) => {
    if (!expressions) return

    const { [Number(index)]: _, ...remainingExpressions } = expressions
    setExpressions(remainingExpressions)
    getFilter(remainingExpressions, index)

    setCanAddNewExpression(true)
  }

  const addNewExpression = () => {
    if (!expressions) return
    const newIndex = Object.keys(expressions).length
    const newExpression: Expression = initialExpression
    setExpressions({ ...expressions, [newIndex]: newExpression })
    setCanAddNewExpression(false)
  }

  const toggleExpand = () => {
    setIsExpanded(!isExpanded)
  }

  return (
    <Container>
      <ExpandableHeader>
        <div className='header'>
          <h3>Filtrar por</h3>
          <hr className='expandable-line' />
          <span
            className='expandable-arrow'
            onClick={e => {
              e.stopPropagation()
              toggleExpand()
            }}
          >
            <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
          </span>
        </div>
        {isExpanded && (
          <>
            <div className='expressions'>
              {expressions &&
                Object.entries(expressions).map(([index, expression]) => {
                  return (
                    <FiltersComponent
                      key={`expression-${index}`}
                      expressionIndex={index}
                      possibleProperties={possibleProperties as PossiblePropertyType[]}
                      isLoading={isLoading}
                      filtersApplied={expression}
                      setNewExpression={handleNewExpression}
                      removeExpression={removeExpression}
                      resizeHeight={resizeHeight}
                    />
                  )
                })}
            </div>
            <ButtonAddExpression
              size='xs'
              onClick={addNewExpression}
              disabled={!canAddNewExpression}
            >
              + Filtros
            </ButtonAddExpression>
          </>
        )}
      </ExpandableHeader>
    </Container>
  )
}

export default ExpressionsContainer
